import { useState, useEffect, useMemo, useCallback } from 'react';

/* eslint frontbucket-patterns/no-new-emotion-styled: "warn" */
import styled from '@emotion/styled';

import { Notifications } from '@atlaskit/atlassian-navigation';
import { NotificationIndicator } from '@atlaskit/notification-indicator';
import { NotificationLogClient } from '@atlaskit/notification-log-client';
import { TriggerProps, Popup } from '@atlaskit/popup';
import NotificationList from '@atlassian/notification-list';

import EmptyNotificationLogClient from './empty-notification-log-client';

const NOTIFICATION_LOG_URL = '/gateway/api/notification-log';
const CLEAR_BADGE_TIME = 10000;
const REFRESH_INTERVAL = 180000; // 3 minutes

const PopupContent = styled.div({
  height: 'calc(100vh - 100px)',
  width: '540px',
  maxWidth: '100vw',
});

const PlatformNotifications = (): JSX.Element => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isCleared, setIsCleared] = useState(false);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (isCleared) {
      const timeout = setTimeout(() => {
        setIsCleared(false);
      }, CLEAR_BADGE_TIME);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [isCleared]);

  const handleClick = useCallback(() => {
    if (isOpen) {
      setIsOpen(false);
    } else {
      setIsOpen(true);
      setIsCleared(true);
    }
  }, [isOpen]);

  const notificationLogClient = useMemo(
    // @ts-ignore
    () => new NotificationLogClient(NOTIFICATION_LOG_URL, undefined),
    []
  );

  const NotificationsBadge = useMemo(
    // eslint-disable-next-line react/display-name
    () => () =>
      (
        <NotificationIndicator
          notificationLogProvider={Promise.resolve(
            isCleared || isOpen
              ? new EmptyNotificationLogClient()
              : notificationLogClient
          )}
          refreshRate={REFRESH_INTERVAL}
          refreshOnHidden
        />
      ),
    [isOpen, isCleared, notificationLogClient]
  );

  const onClose = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  // Renders list of notifications in Popup
  const NotifList = () => (
    <PopupContent data-testid="platform-notif-popup-content">
      <NotificationList />
    </PopupContent>
  );

  // includes bell icon with unseen notification count
  const NotifBellWithCount = useMemo(
    () =>
      (triggerProps: TriggerProps): JSX.Element => {
        return (
          <Notifications
            testId="platform-notif-bell"
            tooltip="Notifications"
            onClick={handleClick}
            isSelected={isOpen}
            badge={NotificationsBadge}
            {...triggerProps}
          />
        );
      },
    [isOpen, handleClick, NotificationsBadge]
  );

  return (
    <Popup
      placement="bottom-end"
      content={NotifList}
      trigger={NotifBellWithCount}
      isOpen={isOpen}
      onClose={onClose}
    />
  );
};

export default PlatformNotifications;
